import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, CheckCircle2, XCircle, Mail, ArrowLeft, Clock } from "lucide-react";
import { SipSetuLogo } from "@/components/SipSetuLogo";
import api from "@/lib/api";
import { useAuth } from "@/app/context/AuthContext";

type PageState = "form" | "loading" | "success" | "error";

// Matches the backend OTP lifetime (10 minutes) and the resend cadence.
const OTP_LIFETIME_SECONDS = 10 * 60;
const RESEND_COOLDOWN_SECONDS = 60;

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const { user, markEmailVerified } = useAuth();
  const [searchParams] = useSearchParams();
  const prefillEmail = searchParams.get("email") || "";

  const [email, setEmail] = useState(prefillEmail);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [state, setState] = useState<PageState>(prefillEmail ? "form" : "form");
  const [message, setMessage] = useState("");
  const [resending, setResending] = useState(false);
  // A fresh code was just sent on signup, so start both timers immediately.
  // Note: the initial expiry assumes the code was just sent; it becomes exact
  // once a resend returns the backend's expires_at.
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [expiresAt, setExpiresAt] = useState<number>(() => Date.now() + OTP_LIFETIME_SECONDS * 1000);
  const [now, setNow] = useState(() => Date.now());

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Tick every second: drives the resend cooldown and the expiry countdown.
  useEffect(() => {
    const id = setInterval(() => {
      setNow(Date.now());
      setResendCooldown((c) => (c > 0 ? c - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const secondsLeft = Math.max(0, Math.floor((expiresAt - now) / 1000));
  const expired = secondsLeft <= 0;

  // Once the code expires, unlock resend immediately so users can get a fresh one.
  useEffect(() => {
    if (expired) setResendCooldown(0);
  }, [expired]);

  const formatCountdown = (totalSeconds: number) => {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = otp.join("");
    if (code.length !== 6) {
      setMessage("Please enter the full 6-digit verification code.");
      return;
    }
    if (!email) {
      setMessage("Please enter your email address.");
      return;
    }

    setState("loading");
    try {
      const response = await api.post("/auth/verify-email", { email, otp: code });
      markEmailVerified();
      setState("success");
      setMessage(response.data.message || "Email verified successfully!");
    } catch (err: any) {
      setState("error");
      setMessage(err?.response?.data?.error || "Verification failed. Please check your code and try again.");
    }
  };

  const handleResend = async () => {
    if (!email || resending || resendCooldown > 0) return;
    setResending(true);
    try {
      // Resend needs auth — if the user isn't logged in, redirect to login
      const res = await api.post("/auth/resend-verification");
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
      setMessage("A new verification code has been sent to your email.");
      setState("form");
      // Restart the cooldown and sync the countdown to the backend's expiry time.
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      const backendExpiresAt = res.data?.expires_at;
      setExpiresAt(
        backendExpiresAt ? new Date(backendExpiresAt).getTime() : Date.now() + OTP_LIFETIME_SECONDS * 1000
      );
    } catch (err: any) {
      if (err?.response?.status === 401) {
        setMessage("Session expired. Please log in to request a new code.");
      } else {
        setMessage(err?.response?.data?.error || "Failed to resend code.");
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50 flex flex-col items-center justify-center p-4">
      <div className="mb-8">
        <SipSetuLogo className="text-3xl font-bold text-[#1E3A5F]" />
      </div>

      <Card className="w-full max-w-md shadow-xl border-0">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto h-14 w-14 rounded-full bg-orange-50 flex items-center justify-center mb-3">
            <Mail className="h-7 w-7 text-[#F97316]" />
          </div>
          <CardTitle className="text-xl font-bold text-slate-900">Verify your email</CardTitle>
          <CardDescription className="text-sm text-slate-500">
            Enter the 6-digit code sent to your email address
          </CardDescription>
        </CardHeader>

        <CardContent className="p-6 pt-2">
          {state === "success" && (
            <div className="py-8 space-y-6 text-center">
              <div className="mx-auto h-16 w-16 rounded-full bg-green-50 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-2">Email Verified! 🎉</h2>
                <p className="text-sm text-slate-500">{message}</p>
              </div>
              <div className="pt-2">
                <Button
                  className="w-full bg-[#F97316] hover:bg-[#e8630e] text-white"
                  onClick={() =>
                    navigate(user?.role === "recruiter" ? "/recruiter/dashboard" : "/applicant/dashboard")
                  }
                >
                  Go to Dashboard
                </Button>
              </div>
            </div>
          )}

          {state === "loading" && (
            <div className="py-8 text-center space-y-6">
              <div className="mx-auto h-16 w-16 rounded-full bg-blue-50 flex items-center justify-center">
                <Loader2 className="h-8 w-8 text-[#1E3A5F] animate-spin" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-2">Verifying...</h2>
                <p className="text-sm text-slate-500">Please wait while we verify your code.</p>
              </div>
            </div>
          )}

          {(state === "form" || state === "error") && (
            <form onSubmit={handleSubmit} className="space-y-5 pt-2">
              {/* Email input */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">Email address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9"
                    required
                  />
                </div>
              </div>

              {/* OTP input row */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">Verification code</label>
                <div className="flex gap-2 justify-center">
                  {otp.map((digit, i) => (
                    <Input
                      key={i}
                      ref={(el) => { inputRefs.current[i] = el; }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(i, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(i, e)}
                      className="w-11 h-12 text-center text-lg font-bold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      autoFocus={i === 0}
                    />
                  ))}
                </div>
              </div>

              {/* Code expiry countdown */}
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                <div className="flex items-center justify-between text-xs">
                  <span className={`flex items-center gap-1.5 font-medium ${expired ? "text-red-600" : "text-slate-600"}`}>
                    <Clock className="h-3.5 w-3.5" />
                    {expired ? "This code has expired" : "Code expires in"}
                  </span>
                  <span className={`font-bold tabular-nums ${expired ? "text-red-600" : "text-[#1E3A5F]"}`}>
                    {expired ? "Expired" : formatCountdown(secondsLeft)}
                  </span>
                </div>
                <div className="mt-2 h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-1000 ${expired ? "bg-red-500" : "bg-[#F97316]"}`}
                    style={{ width: `${(secondsLeft / OTP_LIFETIME_SECONDS) * 100}%` }}
                  />
                </div>
                {expired && (
                  <p className="mt-2 text-xs text-red-600">
                    Request a new code below to continue verifying your email.
                  </p>
                )}
              </div>

              {/* Error message */}
              {state === "error" && message && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
                  <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                  <p className="text-sm text-red-700">{message}</p>
                </div>
              )}

              {/* Submit button */}
              <Button
                type="submit"
                className="w-full bg-[#F97316] hover:bg-[#e8630e] text-white gap-2"
                disabled={expired}
              >
                <CheckCircle2 className="h-4 w-4" />
                Verify Email
              </Button>

              {/* Resend / back links */}
              <div className="flex items-center justify-between text-sm pt-1">
                <button
                  type="button"
                  onClick={() =>
                    navigate(user
                      ? (user.role === "recruiter" ? "/recruiter/dashboard" : "/applicant/dashboard")
                      : "/login")
                  }
                  className="text-slate-500 hover:text-slate-700 flex items-center gap-1"
                >
                  <ArrowLeft className="h-3 w-3" /> Back to dashboard
                </button>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending || !email || resendCooldown > 0}
                  className="text-[#F97316] hover:text-[#e8630e] font-medium disabled:text-slate-300 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  {resending ? (
                    "Sending..."
                  ) : resendCooldown > 0 ? (
                    <>
                      <Clock className="h-3 w-3" /> Resend in {resendCooldown}s
                    </>
                  ) : (
                    "Resend code"
                  )}
                </button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
